export interface DemographicData {
  age?: number;
  ageGroup?: string;
  salaryBracket?: string;
  generationCohort?: string;
}

export function calculateAge(dateOfBirth: string): number | null {
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();

    if (isNaN(birthDate.getTime())) {
      return null;
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  } catch {
    return null;
  }
}

export function getAgeGroup(age: number): string {
  if (age < 18) return "under_18";
  if (age < 25) return "18_24";
  if (age < 35) return "25_34";
  if (age < 45) return "35_44";
  if (age < 55) return "45_54";
  if (age < 65) return "55_64";
  return "65_plus";
}

export function getGenerationCohort(age: number): string {
  const currentYear = new Date().getFullYear();
  const birthYear = currentYear - age;

  if (birthYear >= 1997) return "gen_z";
  if (birthYear >= 1981) return "millennial";
  if (birthYear >= 1965) return "gen_x";
  if (birthYear >= 1946) return "baby_boomer";
  return "silent_generation";
}

export function getSalaryBracket(salary: string | number): string {
  const amount = typeof salary === "string" ? parseFloat(salary) : salary;

  if (isNaN(amount)) return "unknown";

  if (amount < 30000) return "under_30k";
  if (amount < 50000) return "30k_50k";
  if (amount < 75000) return "50k_75k";
  if (amount < 100000) return "75k_100k";
  if (amount < 150000) return "100k_150k";
  if (amount < 200000) return "150k_200k";
  return "over_200k";
}

export function analyzeDemographics(user: {
  DateOfBirth?: string;
  FirstName?: string;
  EmployeeSalary?: string | number;
}): DemographicData {
  const demographics: DemographicData = {};

  if (user.DateOfBirth) {
    const age = calculateAge(user.DateOfBirth);
    if (age !== null) {
      demographics.age = age;
      demographics.ageGroup = getAgeGroup(age);
      demographics.generationCohort = getGenerationCohort(age);
    }
  }

  if (user.EmployeeSalary) {
    demographics.salaryBracket = getSalaryBracket(user.EmployeeSalary);
  }

  return demographics;
}

export function getAnonymizedDemographics(
  demographics: DemographicData,
): Record<string, unknown> {
  return {
    age_group: demographics.ageGroup,
    generation_cohort: demographics.generationCohort,
    salary_bracket: demographics.salaryBracket,
  };
}
